import { useState } from "react";

export const useTemplateForm = () => {
  const [templateForm, setTemplateForm] = useState({
    name: "",
    body: "",
    category: "UTILITY",
    imageUrl: "",
    variables: [],
    ctaButtons: []
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Validation functions
  const validateTemplateName = (name) => {
    const pattern = /^[a-z0-9_]+$/;
    return pattern.test(name);
  };

  const extractVariablesFromBody = (body) => {
    const regex = /\{\{([^}]+)\}\}/g;
    const variables = [];
    let match;
    while ((match = regex.exec(body)) !== null) {
      variables.push(match[1].trim());
    }
    return [...new Set(variables)]; // Remove duplicates
  };

  const validateForm = () => {
    const errors = {};
    
    // Validate template name
    if (!templateForm.name.trim()) {
      errors.name = "Template name is required";
    } else if (!validateTemplateName(templateForm.name)) {
      errors.name = "Template name can only contain lowercase letters, numbers, and underscores";
    }
    
    // Validate template body
    if (!templateForm.body.trim()) {
      errors.body = "Template body is required";
    }
    
    // Validate category
    if (!templateForm.category) {
      errors.category = "Category is required";
    }

    // Validate image URL (optional)
    if (templateForm.imageUrl?.trim()) {
      const trimmedImageUrl = templateForm.imageUrl.trim();
      if (!/^https?:\/\//i.test(trimmedImageUrl)) {
        errors.imageUrl = "Image link must start with https:// or http://. Use variables only in the path (e.g. https://example.com/{{1}}.jpg)";
      } else {
        const imageUrlToValidate = trimmedImageUrl.replace(/\{\{[^}]+\}\}/g, 'placeholder');
        try {
          new URL(imageUrlToValidate);
        } catch (_) {
          errors.imageUrl = "Image link must be a valid URL";
        }
      }
    }
    
    // Validate variables (if any exist)
    const filteredVariables = templateForm.variables.filter(v => v.name.trim() !== "");
    if (filteredVariables.length > 0) {
      // Check if all variables have sample values
      const missingValues = filteredVariables.some(v => !v.sampleValue.trim());
      if (missingValues) {
        errors.variables = "All variables must have sample values";
      }
    }
    
    // Validate CTA buttons
    if (templateForm.ctaButtons && templateForm.ctaButtons.length > 0) {
      if (templateForm.ctaButtons.length > 2) {
        errors.ctaButtons = "Maximum 2 URL buttons allowed";
      } else {
        for (let i = 0; i < templateForm.ctaButtons.length; i++) {
          const btn = templateForm.ctaButtons[i];
          if (!btn.title.trim()) {
            errors.ctaButtons = `Button ${i + 1}: Title is required`;
            break;
          }
          if (btn.title.length > 25) {
            errors.ctaButtons = `Button ${i + 1}: Title must be 25 characters or less`;
            break;
          }
          if (!btn.url.trim()) {
            errors.ctaButtons = `Button ${i + 1}: URL is required`;
            break;
          }
          // Twilio requires CTA button URLs to start with https:// or http://
          // Dynamic variables like {{1}} are only allowed as part of the path, not as the entire URL
          const trimmedUrl = btn.url.trim();
          if (!/^https?:\/\//i.test(trimmedUrl)) {
            errors.ctaButtons = `Button ${i + 1}: URL must start with https:// or http://. Use variables only in the path (e.g. https://example.com/{{1}})`;
            break;
          }
          const urlToValidate = trimmedUrl.replace(/\{\{[^}]+\}\}/g, 'placeholder');
          try {
            new URL(urlToValidate);
          } catch (_) {
            errors.ctaButtons = `Button ${i + 1}: Must be a valid URL`;
            break;
          }
        }
      }
    }

    // Validate variable usage in template body and CTA button URLs
    if (templateForm.body.trim()) {
      const bodyVariables = extractVariablesFromBody(templateForm.body);
      // Also extract variables from CTA button URLs
      const ctaVariables = (templateForm.ctaButtons || []).flatMap(btn => extractVariablesFromBody(btn.url || ''));
      // Also extract variables from image URL
      const imageUrlVariables = extractVariablesFromBody(templateForm.imageUrl || '');
      const usedVariables = [...new Set([...bodyVariables, ...ctaVariables, ...imageUrlVariables])];
      const definedVariables = filteredVariables.map(v => v.name.trim());

      // Check if all used variables are defined
      const undefinedVariables = usedVariables.filter(v => !definedVariables.includes(v));
      if (undefinedVariables.length > 0) {
        errors.body = `Undefined variables used in template: ${undefinedVariables.join(", ")}`;
      }

      // Check if all defined variables are used (only if there are defined variables)
      if (definedVariables.length > 0) {
        const unusedVariables = definedVariables.filter(v => !usedVariables.includes(v));
        if (unusedVariables.length > 0) {
          errors.variables = `Unused variables defined: ${unusedVariables.join(", ")}`;
        }
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setTemplateForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
    if (submitError) {
      setSubmitError("");
    }
  };
  
  const handleVariableChange = (index, field, value) => {
    const updatedVariables = [...templateForm.variables];
    updatedVariables[index] = {
      ...updatedVariables[index],
      [field]: value
    };
    
    setTemplateForm(prev => ({
      ...prev,
      variables: updatedVariables
    }));
    
    // Clear validation error for variables
    if (validationErrors.variables) {
      setValidationErrors(prev => ({
        ...prev,
        variables: undefined
      }));
    }
    if (submitError) {
      setSubmitError("");
    }
  };
  
  const addVariable = () => {
    setTemplateForm(prev => ({
      ...prev,
      variables: [...prev.variables, { name: "", sampleValue: "" }]
    }));
    setSubmitError("");
  };
  
  const removeVariable = (index) => {
    const updatedVariables = [...templateForm.variables];
    updatedVariables.splice(index, 1);

    setTemplateForm(prev => ({
      ...prev,
      variables: updatedVariables
    }));
    setSubmitError("");
  };

  const addCtaButton = () => {
    if (templateForm.ctaButtons.length >= 2) return;
    setTemplateForm(prev => ({
      ...prev,
      ctaButtons: [...prev.ctaButtons, { type: "URL", title: "", url: "" }]
    }));
    setSubmitError("");
  };

  const removeCtaButton = (index) => {
    const updated = [...templateForm.ctaButtons];
    updated.splice(index, 1);
    setTemplateForm(prev => ({
      ...prev,
      ctaButtons: updated
    }));
    setSubmitError("");
  };

  const handleCtaButtonChange = (index, field, value) => {
    const updated = [...templateForm.ctaButtons];
    updated[index] = { ...updated[index], [field]: value };
    setTemplateForm(prev => ({
      ...prev,
      ctaButtons: updated
    }));
    if (validationErrors.ctaButtons) {
      setValidationErrors(prev => ({ ...prev, ctaButtons: undefined }));
    }
    if (submitError) {
      setSubmitError("");
    }
  };

  const resetForm = () => {
    setTemplateForm({
      name: "",
      body: "",
      category: "UTILITY",
      imageUrl: "",
      variables: [],
      ctaButtons: []
    });
    setValidationErrors({});
    setFormSubmitting(false);
    setSubmitError("");
  };

  const loadTemplateData = (template) => {
    // Extract variables from template fields
    const variables = template.fields.map(field => ({
      name: field,
      sampleValue: ""
    }));

    setTemplateForm({
      name: template.name,
      body: template.value,
      category: template.metadata?.category || "UTILITY",
      imageUrl: template.metadata?.imageUrl || "",
      variables: variables,
      ctaButtons: template.metadata?.ctaButtons || []
    });
    setValidationErrors({});
    setSubmitError("");
  };

  const getFormData = () => {
    // Filter out empty variables
    const filteredVariables = templateForm.variables.filter(v => v.name.trim() !== "");
    // Filter out empty CTA buttons
    const filteredCtaButtons = templateForm.ctaButtons.filter(b => b.title.trim() && b.url.trim());

    return {
      name: templateForm.name,
      body: templateForm.body,
      category: templateForm.category,
      language: "en", // Hardcoded as requested
      imageUrl: templateForm.imageUrl?.trim() || undefined,
      variables: filteredVariables,
      ctaButtons: filteredCtaButtons.length > 0 ? filteredCtaButtons : undefined
    };
  };

  return {
    templateForm,
    validationErrors,
    formSubmitting,
    submitError,
    setFormSubmitting,
    setSubmitError,
    validateForm,
    handleFormChange,
    handleVariableChange,
    addVariable,
    removeVariable,
    addCtaButton,
    removeCtaButton,
    handleCtaButtonChange,
    resetForm,
    loadTemplateData,
    getFormData
  };
}; 
